/**
 * The canonical form a content fingerprint is taken over.
 *
 * Both creator studios need the same answer to *has anything actually changed since
 * the last save?* — the FlashBook's book and the Capsule's slides. Remembering a
 * boolean is how these guards go wrong in both directions: a stray keystroke that
 * was then undone marks the work dirty forever, and simply opening a draft marks it
 * dirty immediately because the fields were populated. So each studio fingerprints
 * its whole content and compares; an untouched draft recomputes to the same string
 * and the guard stays quiet, and any real edit changes it.
 *
 * That fingerprint has to be over a *canonical* form, and this is it.
 * `JSON.stringify` prints object keys in insertion order, so two objects holding
 * the same data in a different order produce different strings. Work that went
 * through a save, a reload, or another client can easily come back with its keys
 * reordered — and a fingerprint that calls that "unsaved changes" is a guard that
 * cries wolf, which is the failure mode that gets guards ignored.
 *
 * Shared rather than written twice for the usual reason, and one more: the two
 * studios' fingerprints have to agree about what "the same content" means, or the
 * same draft saved by both would look changed to whichever one opened it second.
 */
export function canonicalContent(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalContent)
  if (value && typeof value === 'object') {
    const source = value as Record<string, unknown>
    const out: Record<string, unknown> = {}
    for (const key of Object.keys(source).sort()) {
      // `undefined` is dropped by `JSON.stringify` either way, so a block that
      // gained an empty field is correctly not a change.
      if (source[key] === undefined) continue
      out[key] = canonicalContent(source[key])
    }
    return out
  }
  return value
}
